import { Component } from "react";

/**
 * Class-based error boundary (React has no hook equivalent). Catches render
 * errors in its subtree and swaps in a friendly themed card instead of
 * white-screening the whole app.
 *
 * Used twice:
 *   - Inside Shell, wrapped around the routed <Outlet /> and keyed on
 *     location.pathname — a crash on one page leaves the sidebar alive, and
 *     navigating to another route remounts the boundary (fresh state) so the
 *     app recovers without a full reload.
 *   - Around the whole app in main.jsx as a last resort for crashes outside
 *     the Shell (login pages, providers).
 *
 * Props:
 *   children : ReactNode
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the stack in the console for debugging — the UI shows a collapsed
    // summary, but the full componentStack only lives here.
    console.error("ErrorBoundary caught:", error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="bg-surface border border-border-default rounded-card p-8 text-center max-w-md mx-auto mt-10">
        <div className="w-10 h-10 rounded-full bg-[#fef2f2] flex items-center justify-center mx-auto mb-3">
          <span className="text-negative text-[16px] font-bold">!</span>
        </div>
        <div className="text-[13px] font-semibold text-text-primary mb-1">
          Something went wrong on this page
        </div>
        <div className="text-[12px] text-text-secondary mb-4">
          The rest of the app is still running — try reloading, or use the
          sidebar to go somewhere else.
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="text-[12px] text-accent hover:underline font-medium"
        >
          Reload
        </button>
        <details className="mt-4 text-left">
          <summary className="text-[11px] text-text-muted cursor-pointer select-none">
            Error detail
          </summary>
          <pre className="mt-2 p-2 rounded bg-app-bg border border-border-light text-[10px] text-text-secondary overflow-x-auto whitespace-pre-wrap break-words">
            {String(error?.stack || error?.message || error)}
          </pre>
        </details>
      </div>
    );
  }
}
